import { useEffect, useRef } from 'react';
import { assetAvifSrcSet, assetVariantSrcSet, assetVariantUrl, THUMBNAIL_VARIANT_WIDTH, } from './assetVariant.js';
import { avifSupported } from './avifSupport.js';
import { imagesForVariant } from './productImages.js';
/** Ce qui a DÉJÀ été demandé — une même photo n'est jamais préchauffée deux fois dans la page. */
const requested = new Set();
/** Les images en vol, gardées vivantes le temps du chargement (rien d'autre ne les référence). */
const inFlight = new Set();
function targetKey(t) {
    return `${t.url}|${t.sizes ?? ''}`;
}
/**
 * Le navigateur annonce-t-il une connexion qu'on n'a pas le droit de dépenser sans geste de l'acheteuse ?
 *
 * Ce que coûte le balayage, mesuré sur la vraie préprod : une photo de la page MOVE pèse 79 Ko au palier
 * 960 (celui d'un bureau) et davantage sur un téléphone, dont l'écran 3× réclame le palier supérieur. Six
 * couleurs de plus, c'est un demi-mégaoctet. En wifi ou en 4G c'est le prix normal d'une page produit
 * moderne ; en 3G marocaine, c'est une dépense qu'on impose à quelqu'un qui n'a peut-être jamais
 * l'intention de changer de couleur. Là, on ne préchauffe plus qu'au geste : survol, focus, doigt posé.
 *
 * `saveData` est un choix explicite de l'acheteuse et ne se discute pas.
 */
export function warmingIsUnwelcome() {
    if (typeof navigator === 'undefined')
        return true;
    const conn = navigator.connection;
    if (!conn)
        return false;
    if (conn.saveData === true)
        return true;
    const type = conn.effectiveType;
    return type === '2g' || type === 'slow-2g' || type === '3g';
}
/**
 * Combien de couleurs le balayage va chercher au plus. Une boutique de bijoux à vingt coloris ferait
 * sinon télécharger deux mégaoctets à quelqu'un qui n'a rien demandé ; au-delà de ce plafond, les
 * couleurs suivantes restent couvertes par le geste (survol / doigt posé), qui ne rate jamais.
 */
export const WARM_SWEEP_LIMIT = 8;
/**
 * Demander une image sans l'afficher. Le navigateur la range dans son cache : le `<img>` de la galerie
 * qui pointera dessus au clic suivant la trouve sans aller-retour, et peint à l'image d'après.
 */
export async function warmImage(target, priority = 'low') {
    if (typeof window === 'undefined' || !target.url)
        return;
    const key = targetKey(target);
    if (requested.has(key))
        return;
    requested.add(key);
    // Feature 0067 : la galerie peint l'AVIF quand le navigateur le décode (`<picture>`). Préchauffer le
    // WebP ici ferait voyager la photo deux fois — on demande le format qui sera réellement peint.
    const srcSet = target.avifSrcSet && (await avifSupported()) ? target.avifSrcSet : target.srcSet;
    return new Promise((resolve) => {
        const img = new Image();
        inFlight.add(img);
        const settle = (ok) => {
            inFlight.delete(img);
            // Un échec ne se grave pas : la photo pourra être retentée plus tard (au survol, au clic), sans
            // quoi un hoquet passager condamnerait cette couleur à rester lente pour toute la visite.
            if (!ok)
                requested.delete(key);
            resolve();
        };
        img.addEventListener('load', () => settle(true), { once: true });
        img.addEventListener('error', () => settle(false), { once: true });
        img.decoding = 'async';
        img.fetchPriority = priority;
        // `sizes` et `srcset` AVANT `src` : posés après, le navigateur a déjà choisi son candidat sur le
        // seul `src` et on aurait préchauffé l'original de 2560px au lieu du palier que la galerie peint.
        if (target.sizes)
            img.sizes = target.sizes;
        if (srcSet)
            img.srcset = srcSet;
        img.src = target.url;
    });
}
/** La file du balayage : une image à la fois, jamais une rafale vers le stockage. */
let chain = Promise.resolve();
export function warmInTurn(target) {
    if (typeof window === 'undefined' || requested.has(targetKey(target)))
        return;
    chain = chain.then(() => warmImage(target, 'low'));
}
/** Remise à zéro — les tests seulement : l'état vit à l'échelle du module, donc d'une page. */
export function resetWarmedImages() {
    requested.clear();
    inFlight.clear();
    chain = Promise.resolve();
}
const warmers = new Map();
/** La clé d'un produit — son id, le slug pour les produits de démonstration qui n'en ont pas. */
export function productWarmKey(product) {
    return product.id || product.slug || 'default';
}
export function registerVariantWarmer(key, fn) {
    let set = warmers.get(key);
    if (!set) {
        set = new Set();
        warmers.set(key, set);
    }
    set.add(fn);
    return () => {
        set?.delete(fn);
        if (set && set.size === 0)
            warmers.delete(key);
    };
}
/** Appelé par le sélecteur au survol / focus / premier contact : « cette couleur-là, maintenant ». */
export function warmVariantImages(key, optionValueLabel) {
    warmers.get(key)?.forEach((fn) => fn(optionValueLabel));
}
/* ------------------------------------------------------------------------------------------------ */
/** La première photo de CHAQUE couleur photographiée, dans l'ordre où la vendeuse les a rangées. */
export function firstImagePerGroup(product) {
    const seen = new Set();
    const out = [];
    for (const img of product.images ?? []) {
        const group = img.optionValue?.trim().toLowerCase();
        if (!group || seen.has(group))
            continue;
        seen.add(group);
        out.push(img);
    }
    return out;
}
/** `requestIdleCallback` après `load`, avec repli minuterie — rend de quoi tout annuler au démontage. */
function afterLoadWhenIdle(run) {
    let cancelled = false;
    let idleId = null;
    let timerId = null;
    const win = window;
    const schedule = () => {
        if (cancelled)
            return;
        if (typeof win.requestIdleCallback === 'function') {
            idleId = win.requestIdleCallback(() => {
                if (!cancelled)
                    run();
            }, { timeout: 3000 });
        }
        else {
            timerId = setTimeout(() => {
                if (!cancelled)
                    run();
            }, 1200);
        }
    };
    if (document.readyState === 'complete')
        schedule();
    else
        window.addEventListener('load', schedule, { once: true });
    return () => {
        cancelled = true;
        window.removeEventListener('load', schedule);
        if (idleId !== null && typeof win.cancelIdleCallback === 'function')
            win.cancelIdleCallback(idleId);
        if (timerId !== null)
            clearTimeout(timerId);
    };
}
/**
 * Monté PAR LA GALERIE (elle seule connaît `sizes`) : publie le préchauffeur pour le sélecteur, puis
 * balaie les autres couleurs au repos. Ne peint rien.
 */
export function VariantImageWarmer({ product, sizes, }) {
    // Le produit change d'identité à chaque rendu dans le builder ; ce qui compte pour l'effet, c'est sa
    // LISTE DE PHOTOS. Sans cette ref + signature, l'effet se relancerait à chaque frappe de la vendeuse.
    const latest = useRef(product);
    latest.current = product;
    const key = productWarmKey(product);
    const signature = (product.images ?? []).map((i) => `${i.optionValue ?? ''}:${i.url}`).join('|');
    useEffect(() => {
        const warmLabel = (label) => {
            const images = imagesForVariant(latest.current, label);
            const first = images[0];
            if (!first)
                return;
            warmImage({
                url: first.url,
                srcSet: assetVariantSrcSet(first.url),
                avifSrcSet: assetAvifSrcSet(first.url),
                sizes,
            }, 'high');
            // La bande de vignettes se repeint elle aussi au changement de couleur — 160px pièce, quelques
            // kilo-octets : on ne les préchauffe qu'ici, sur geste, jamais dans le balayage (7 couleurs × 7
            // vignettes, ce serait un quart de mégaoctet demandé à une acheteuse qui n'a encore rien cliqué).
            for (const img of images.slice(0, 8)) {
                warmImage({ url: assetVariantUrl(img.url, THUMBNAIL_VARIANT_WIDTH) }, 'low');
            }
        };
        const unregister = registerVariantWarmer(key, warmLabel);
        if (warmingIsUnwelcome())
            return unregister;
        const cancel = afterLoadWhenIdle(() => {
            for (const img of firstImagePerGroup(latest.current).slice(0, WARM_SWEEP_LIMIT)) {
                warmInTurn({
                    url: img.url,
                    srcSet: assetVariantSrcSet(img.url),
                    avifSrcSet: assetAvifSrcSet(img.url),
                    sizes,
                });
            }
        });
        return () => {
            unregister();
            cancel();
        };
    }, [key, signature, sizes]);
    return null;
}
//# sourceMappingURL=imageWarm.js.map